import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type React from "react"
import { useMemo, useState } from "react"
import { FiSearch } from "react-icons/fi"
import { z } from "zod"

import { ClassSessionActionsMenu } from "@/components/layout/Common/ClassSessionActionsMenu"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import {
  ClassCalendar,
  type EnrichedClassSession,
} from "@/components/organisms/ClassCalendar"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import {
  AddClassSession,
  GenerateClassSessions,
} from "@/features/classSessions"
import { usePermissions } from "@/hooks/usePermissions"
import { api } from "@/services/api"
import type {
  ClassSessionListResponse,
  ClassSessionResponse,
} from "@/types/class_session"
import { unwrapStandardResponse } from "@/utils/apiResponse"

const classSessionsSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1),
  search: z.string().optional(),
  sort: z.enum(["created_at", "updated_at"]).catch("created_at"),
  direction: z.enum(["asc", "desc"]).catch("desc"),
})

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

function getClassSessionsQueryOptions({
  page,
  search,
  sort,
  direction,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
}) {
  const debugClassSessions =
    import.meta.env.DEV && import.meta.env.VITE_DEBUG_CLASSMEETINGS === "true"

  return {
    queryFn: async () => {
      if (debugClassSessions) {
        console.log(
          `ClassSessions page: Fetching page ${page} with ${PER_PAGE} records per page`,
        )
      }

      const response =
        await api.classSessions.readClassSessionsApiV1ClassSessionsGet({
          skip: (page - 1) * PER_PAGE,
          limit: PER_PAGE,
          search,
          sortBy: sort,
          sortOrder: direction,
        })

      const payload =
        unwrapStandardResponse<ClassSessionListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      if (debugClassSessions) {
        console.log("ClassSessions page: API payload:", payload)
        console.log("🔍 ClassSessions page: items count:", items.length)
        console.log("🔍 ClassSessions page: total:", total)
      }

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "classSessions",
      {
        page,
        search,
        sort,
        direction,
      },
    ],
  }
}

export const Route = createFileRoute("/_layout/classSessions")({
  component: ClassSessions,
  validateSearch: (search) => classSessionsSearchSchema.parse(search),
})

function ClassSessions() {
  const navigate = useNavigate({ from: Route.fullPath })
  const { page, search, sort, direction } = Route.useSearch()
  const { hasPermission } = usePermissions()
  const [searchInput, setSearchInput] = useState(search ?? "")
  const [viewMode, setViewMode] = useState<ViewMode>("table")

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getClassSessionsQueryOptions({
      page,
      search: search || undefined,
      sort,
      direction,
    }),
    placeholderData: (previous) => previous,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage,
      }),
    })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    navigate({
      search: (previous) => {
        const { search: _prevSearch, ...rest } = previous
        return nextValue
          ? { ...rest, page: 1, search: nextValue }
          : { ...rest, page: 1 }
      },
    })
  }

  const handleSortChange = (nextSort: SortField) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        sort: nextSort,
      }),
    })
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        direction: nextDirection,
      }),
    })
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("class_sessions:update") ||
    hasPermission("class_sessions:delete")

  const columns = useMemo<DataTableColumn<ClassSessionResponse>[]>(() => {
    const tableColumns: DataTableColumn<ClassSessionResponse>[] = []

    // User-friendly columns showing names instead of IDs
    tableColumns.push({
      key: "class_name",
      label: "Class",
      render: (classSession) => (
        <Text fontWeight="medium">
          {classSession.class_name ||
            formatFieldValue(classSession.class_template_id, "str")}
        </Text>
      ),
    })

    tableColumns.push({
      key: "session_name",
      label: "Season",
      render: (classSession) => (
        <Text>{classSession.session_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "session_date",
      label: "Date",
      render: (classSession) => (
        <Text>{formatFieldValue(classSession.session_date, "date")}</Text>
      ),
    })

    tableColumns.push({
      key: "time",
      label: "Time",
      render: (classSession) => {
        const start = new Date(classSession.session_start)
        const end = new Date(classSession.session_end)
        return (
          <Text>
            {start.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            -{" "}
            {end.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </Text>
        )
      },
    })

    tableColumns.push({
      key: "instructor_name",
      label: "Instructor",
      render: (classSession) => (
        <Text>{classSession.instructor_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "location",
      label: "Location",
      render: (classSession) => (
        <Text>{classSession.location || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "status",
      label: "Status",
      render: (classSession) => {
        const status = classSession.status || "scheduled"
        const colorScheme =
          status === "scheduled"
            ? "blue"
            : status === "in_progress"
              ? "yellow"
              : status === "completed"
                ? "green"
                : status === "cancelled"
                  ? "red"
                  : status === "rescheduled"
                    ? "purple"
                    : "gray"

        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={`${colorScheme}.50`}
            color={`${colorScheme}.700`}
          >
            {status}
          </Box>
        )
      },
    })

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (classSession) => (
          <ClassSessionActionsMenu classSession={classSession} />
        ),
      })
    }

    return tableColumns
  }, [showActions])

  if (isLoading) {
    return (
      <Container maxW="full">
        <Flex justifyContent="space-between" alignItems="center" pt={12} mb={6}>
          <Heading size="lg">Class Schedule</Heading>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading class meetings...</Text>
        </Flex>
      </Container>
    )
  }

  return (
    <Container maxW="full">
      {/* Header */}
      <Flex
        direction={{ base: "column", md: "row" }}
        justify={{ base: "flex-start", md: "space-between" }}
        align={{ base: "stretch", md: "center" }}
        pt={12}
        mb={6}
        gap={{ base: 4, md: 0 }}
      >
        {/* Title row with view toggle and mobile-only action buttons */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "flex-start", sm: "center" }}
          gap={{ base: 3, sm: 4 }}
          width={{ base: "100%", md: "auto" }}
        >
          <HStack gap={4}>
            <Heading size="lg">Class Schedule</Heading>
            <ViewToggle
              value={viewMode}
              onChange={setViewMode}
              modes={["table", "calendar"]}
            />
          </HStack>
          {/* Mobile-only action buttons */}
          <HStack gap={2} display={{ base: "flex", md: "none" }}>
            <GenerateClassSessions />
            <AddClassSession />
          </HStack>
        </Flex>

        {/* Controls row with search/sort (table mode) and desktop action buttons */}
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
          width={{ base: "100%", md: "auto" }}
        >
          {viewMode === "table" && (
            <>
              <form onSubmit={handleSearchSubmit} style={{ width: "100%" }}>
                <HStack gap={2} width={{ base: "100%", md: "auto" }}>
                  <Input
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Search class meetings"
                    size="sm"
                    width={{ base: "100%", md: "220px" }}
                    aria-label="Search class meetings"
                  />
                  <Button type="submit" size="sm" colorPalette="blue">
                    Search
                  </Button>
                </HStack>
              </form>
              <Box
                {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
                as="select"
                size="sm"
                value={sort}
                onChange={(event) =>
                  handleSortChange(
                    (event.target as HTMLSelectElement).value as SortField,
                  )
                }
                aria-label="Sort class meetings"
                width={{ base: "100%", md: "170px" }}
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                px={3}
                py={1}
                fontSize="sm"
                bg="white"
                _hover={{ borderColor: "gray.300" }}
                _focus={{
                  borderColor: "blue.500",
                  outline: "none",
                  boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
                }}
              >
                <option value="created_at">Newest</option>
                <option value="updated_at">Recently updated</option>
              </Box>
              <Box
                {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
                as="select"
                size="sm"
                value={direction}
                onChange={(event) =>
                  handleDirectionChange(
                    (event.target as HTMLSelectElement).value as SortDirection,
                  )
                }
                aria-label="Sort direction"
                width={{ base: "100%", md: "150px" }}
                borderRadius="md"
                borderWidth="1px"
                borderColor="gray.200"
                px={3}
                py={1}
                fontSize="sm"
                bg="white"
                _hover={{ borderColor: "gray.300" }}
                _focus={{
                  borderColor: "blue.500",
                  outline: "none",
                  boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
                }}
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </Box>
            </>
          )}
          {/* Desktop-only action buttons */}
          <HStack gap={2} display={{ base: "none", md: "flex" }}>
            <GenerateClassSessions />
            <AddClassSession />
          </HStack>
        </Flex>
      </Flex>

      {viewMode === "calendar" ? (
        <Box>
          <ClassCalendar
            sessions={records.map(
              (session: ClassSessionResponse) =>
                ({
                  ...session,
                  class_name: session.class_name,
                  swim_level: session.swim_level,
                  location: session.location,
                  instructor_name: session.instructor_name,
                }) as EnrichedClassSession,
            )}
          />
          {count > PER_PAGE && (
            <Text fontSize="sm" color="gray.500" textAlign="center" mt={4}>
              Showing {records.length} of {count} meetings. Switch to table view
              to see all meetings with pagination.
            </Text>
          )}
        </Box>
      ) : records.length === 0 ? (
        <VStack gap={4} py={12} alignItems="center">
          <FiSearch size={48} color="gray" />
          <VStack textAlign="center" gap={2}>
            <Heading size="md" color="gray.600">
              You don't have any class meetings yet
            </Heading>
            <Text color="gray">Add a new class meeting to get started</Text>
          </VStack>
        </VStack>
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            <DataTable columns={columns} data={records} size="sm" />
          </Box>
          <Flex
            justifyContent={{ base: "center", md: "flex-end" }}
            mt={4}
            gap={2}
          >
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </Container>
  )
}
